import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { EditorType } from '@factories/app-editor-factory/enum/editor-type';
import type { IFilterValues } from '@pages/reports/types/filter-values.interface';
import type { IFilterItem } from '@pages/reports/types/report-config.interface';
import { ReportFiltersStateService } from '@services/report-filters-state.service';
import { ReportSingleService } from '@services/report-single.service';
import { DateRangeFormEditorComponent } from '@shared/form-editors/date-range-form-editor/date-range-form-editor.component';
import { DropdownRangesFormEditorComponent } from '@shared/form-editors/dropdown-ranges-form-editor/dropdown-ranges-form-editor.component';
import { IncludeArchivedFormEditorComponent } from '@shared/form-editors/include-archived-form-editor/include-archived-form-editor.component';
import { ReportAccountFormEditorComponent } from '@shared/form-editors/report/report-account-form-editor/report-account-form-editor.component';
import { ReportBidCategoriesFormEditorComponent } from '@shared/form-editors/report/report-bid-categories-form-editor/report-bid-categories-form-editor.component';
import { ReportBuildingClassFormEditorComponent } from '@shared/form-editors/report/report-building-class-form-editor/report-building-class-form-editor.component';
import { ReportChangeOrderStatusFormEditorComponent } from '@shared/form-editors/report/report-change-order-status-form-editor/report-change-order-status-form-editor.component';
import { ReportCompanyFormEditorComponent } from '@shared/form-editors/report/report-company-form-editor/report-company-form-editor.component';
import { ReportCompanyTypeFormEditorComponent } from '@shared/form-editors/report/report-company-type-form-editor/report-company-type-form-editor.component';
import { ReportCompetingBidderFormEditorComponent } from '@shared/form-editors/report/report-competing-bidder-form-editor/report-competing-bidder-form-editor.component';
import { ReportDepartmentFormEditorComponent } from '@shared/form-editors/report/report-department-form-editor/report-department-form-editor.component';
import { ReportEstimateTypeFormEditorComponent } from '@shared/form-editors/report/report-estimate-type-form-editor/report-estimate-type-form-editor.component';
import { ReportMarketFormEditorComponent } from '@shared/form-editors/report/report-market-form-editor/report-market-form-editor.component';
import { ReportOpportunityCompanyRoleFormEditorComponent } from '@shared/form-editors/report/report-opportunity-company-role-form-editor/report-opportunity-company-role-form-editor.component';
import { ReportOpportunityProductTypesFormEditorComponent } from '@shared/form-editors/report/report-opportunity-product-types-form-editor/report-opportunity-product-types-form-editor.component';
import { ReportOpportunitySourceFormEditorComponent } from '@shared/form-editors/report/report-opportunity-source-form-editor/report-opportunity-source-form-editor.component';
import { ReportOpportunityStatusFormEditorComponent } from '@shared/form-editors/report/report-opportunity-status-form-editor/report-opportunity-status-form-editor.component';
import { ReportProjectStatusFormEditorComponent } from '@shared/form-editors/report/report-project-status-form-editor/report-project-status-form-editor.component';
import { ReportStateFormEditorComponent } from '@shared/form-editors/report/report-state-form-editor/report-state-form-editor.component';
import { ReportUserFormEditorComponent } from '@shared/form-editors/report/report-user-form-editor/report-user-form-editor.component';
import { GenericOverlayComponent } from '@shared/generic-overlay/generic-overlay.component';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import { IconSanitizerService } from 'ngx-lib';

@Component({
    selector: 'app-report-additional-filters-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        GenericOverlayComponent,
        DateRangeFormEditorComponent,
        DropdownRangesFormEditorComponent,
        IncludeArchivedFormEditorComponent,
        ReportDepartmentFormEditorComponent,
        ReportEstimateTypeFormEditorComponent,
        ReportCompanyFormEditorComponent,
        ReportAccountFormEditorComponent,
        ReportCompanyTypeFormEditorComponent,
        ReportCompetingBidderFormEditorComponent,
        ReportBidCategoriesFormEditorComponent,
        ReportBuildingClassFormEditorComponent,
        ReportOpportunityCompanyRoleFormEditorComponent,
        ReportOpportunityProductTypesFormEditorComponent,
        ReportUserFormEditorComponent,
        ReportStateFormEditorComponent,
        ReportOpportunityStatusFormEditorComponent,
        ReportProjectStatusFormEditorComponent,
        ReportChangeOrderStatusFormEditorComponent,
        ReportMarketFormEditorComponent,
        ReportOpportunitySourceFormEditorComponent,
        ...CDK_IMPORTS
    ],
    templateUrl: './report-additional-filters-form.component.html',
    styleUrls: ['./report-additional-filters-form.component.scss']
})
export class ReportAdditionalFiltersFormComponent implements OnInit {
    private readonly service = inject(ReportSingleService);
    private readonly reportFiltersStateService = inject(ReportFiltersStateService);
    private readonly iconSanitizer = inject(IconSanitizerService);

    readonly EditorType = EditorType;
    readonly form = new FormGroup({
        projectDueDate: new FormControl<{ from?: Date | null; to?: Date | null } | null>(null),
        opportunityCreationDate: new FormControl<{
            from?: Date | null;
            to?: Date | null;
            includeEmpty?: boolean | null;
        } | null>(null),
        projectStartDate: new FormControl<{
            from?: Date | null;
            to?: Date | null;
            includeEmpty?: boolean | null;
        } | null>(null),
        projectEndDate: new FormControl<{ from?: Date | null; to?: Date | null; includeEmpty?: boolean | null } | null>(
            null
        ),
        projectStatusIds: new FormControl<number[] | null>(null),
        changeOrderStatusIds: new FormControl<number[] | null>(null),
        sellPriceValue: new FormControl<{ from?: number | null; to?: number | null } | null>(null),
        markets: new FormControl<number[] | null>(null),
        leads: new FormControl<number[] | null>(null),
        divisionIds: new FormControl<number[] | null>(null),
        customerIds: new FormControl<number[] | null>(null),
        companyIds: new FormControl<number[] | null>(null),
        accountIds: new FormControl<number[] | null>(null),
        companyTypeId: new FormControl<number | null>(null),
        relatedOpportunitiesSalesPersonIds: new FormControl<number[] | null>(null),
        opportunityStatusIds: new FormControl<number[] | null>(null),
        productTypesIds: new FormControl<number[] | null>(null),
        companyRoleIds: new FormControl<number[] | null>(null),
        competingBidderIds: new FormControl<number[] | null>(null),
        bidCategoryIds: new FormControl<number[] | null>(null),
        opportunityDueDate: new FormControl<{
            from?: Date | null;
            to?: Date | null;
            includeEmpty?: boolean | null;
        } | null>(null),
        expectedResponseDate: new FormControl<{
            from?: Date | null;
            to?: Date | null;
            includeEmpty?: boolean | null;
        } | null>(null),
        potentialValue: new FormControl<{ from?: number | null; to?: number | null } | null>(null),
        probabilityValue: new FormControl<{ from?: number | null; to?: number | null } | null>(null),
        includeArchived: new FormControl<boolean | null>(null),
        includeOpportunityArchived: new FormControl<boolean | null>(null),
        stateId: new FormControl<number | null>(null),
        buildingClassId: new FormControl<number | null>(null),
        bidDate: new FormControl<{ from?: Date | null; to?: Date | null } | null>(null),
        awardedDate: new FormControl<{ from?: Date | null; to?: Date | null } | null>(null),
        bidAmount: new FormControl<{ from?: number | null; to?: number | null } | null>(null),
        awardedAmount: new FormControl<{ from?: number | null; to?: number | null } | null>(null),
        estimateTypeId: new FormControl<number | null>(null)
    });

    readonly opened = signal(false);
    readonly additionalEditorsEpoch = signal(0);
    readonly additionalFilterItems = computed(() => this.service.configuration()?.filters.additional ?? []);
    readonly hasAppliedFilters = computed(() => {
        const values = this.service.filterValues();
        const appliedValues: Record<string, unknown> = {};
        for (const item of this.additionalFilterItems()) {
            const key = this.getControlName(item);
            appliedValues[key] = (values as Record<string, unknown>)[key];
        }
        return this.hasAnySelectedFilter(appliedValues);
    });
    readonly canClearAllFilters = computed(() => {
        const values = this.service.filterValues();
        const appliedValues: Record<string, unknown> = {};
        for (const item of this.additionalFilterItems()) {
            const key = this.getControlName(item);
            appliedValues[key] = (values as Record<string, unknown>)[key];
        }
        return this.hasAnySelectedFilter(appliedValues, true);
    });

    closeIcon?: SafeHtml;
    additionalFiltersIcon?: SafeHtml;
    appliedAdditionalFiltersIcon?: SafeHtml;

    ngOnInit(): void {
        this.closeIcon = this.iconSanitizer.getIcon('close');
        this.additionalFiltersIcon = this.iconSanitizer.getIcon('additionalFilters');
        this.appliedAdditionalFiltersIcon = this.iconSanitizer.getIcon('appliedAdditionalFilters');
        this.patchFromCurrentFilters();
    }

    onOverlayChange(isOpen: boolean): void {
        this.opened.set(isOpen);
        if (isOpen) {
            this.patchFromCurrentFilters();
            this.additionalEditorsEpoch.update((n) => n + 1);
            return;
        }

        this.persistAdditionalFilters();
    }

    onApply(close: () => void): void {
        this.persistAdditionalFilters();
        close();
    }

    onClearFilters(close: () => void): void {
        this.form.reset({
            projectDueDate: null,
            opportunityCreationDate: { from: null, to: null, includeEmpty: null },
            projectStartDate: { from: null, to: null, includeEmpty: null },
            projectEndDate: { from: null, to: null, includeEmpty: null },
            projectStatusIds: null,
            changeOrderStatusIds: null,
            sellPriceValue: null,
            markets: null,
            leads: null,
            divisionIds: null,
            customerIds: null,
            companyIds: null,
            accountIds: null,
            companyTypeId: null,
            relatedOpportunitiesSalesPersonIds: null,
            opportunityStatusIds: null,
            productTypesIds: null,
            companyRoleIds: null,
            competingBidderIds: null,
            bidCategoryIds: null,
            opportunityDueDate: { from: null, to: null, includeEmpty: null },
            expectedResponseDate: { from: null, to: null, includeEmpty: null },
            potentialValue: null,
            probabilityValue: null,
            includeArchived: null,
            includeOpportunityArchived: null,
            stateId: null,
            buildingClassId: null,
            bidDate: null,
            awardedDate: null,
            bidAmount: null,
            awardedAmount: null,
            estimateTypeId: null
        });
        const cleared = this.buildAdditionalFilters();
        this.service.updateFilterValues(cleared);
        this.reportFiltersStateService.setAdditionalFilters(cleared);
        this.additionalEditorsEpoch.update((n) => n + 1);
        close();
    }

    getControlName(item: IFilterItem): string {
        if (item.fieldName === 'salePriceValue') return 'sellPriceValue';
        return item.fieldName;
    }

    getLabel(item: IFilterItem): string {
        return item.label ?? '';
    }

    getPlaceholder(item: IFilterItem): string {
        const custom = (item as IFilterItem & { additionalInfo?: { placeholder?: string } }).additionalInfo
            ?.placeholder;
        if (custom) return custom;
        const action = item.componentId === EditorType.dateRange ? 'Add' : 'Select';
        return item.label ? `${action} ${item.label}` : action;
    }

    shouldAllowSelectEmptyDate(item: IFilterItem): boolean {
        if (this.service.reportTemplateName() !== 'opportunities') return false;

        return (
            item.fieldName === 'opportunityDueDate' ||
            item.fieldName === 'opportunityCreationDate' ||
            item.fieldName === 'projectStartDate' ||
            item.fieldName === 'projectEndDate' ||
            item.fieldName === 'expectedResponseDate'
        );
    }

    getIconType(item: IFilterItem): 'dollar' | 'percentage' {
        const icon = (item as IFilterItem & { additionalInfo?: { iconType?: string } }).additionalInfo?.iconType;
        return icon === 'percentage' ? 'percentage' : 'dollar';
    }

    private patchFromCurrentFilters(): void {
        const current = this.service.filterValues();
        this.form.patchValue(
            {
                projectStatusIds: current.projectStatusIds ?? null,
                changeOrderStatusIds: current.changeOrderStatusIds ?? null,
                projectDueDate: current.projectDueDate ?? null,
                opportunityCreationDate: current.opportunityCreationDate ?? null,
                projectStartDate: current.projectStartDate ?? null,
                projectEndDate: current.projectEndDate ?? null,
                sellPriceValue: current.sellPriceValue ?? null,
                markets: current.markets ?? null,
                leads: current.leads ?? null,
                divisionIds: current.divisionIds ?? null,
                customerIds: current.customerIds ?? null,
                companyIds: current.companyIds ?? null,
                accountIds: current.accountIds ?? null,
                companyTypeId: current.companyTypeId ?? null,
                relatedOpportunitiesSalesPersonIds: current.relatedOpportunitiesSalesPersonIds ?? null,
                opportunityStatusIds: current.opportunityStatusIds ?? null,
                productTypesIds: current.productTypesIds ?? null,
                companyRoleIds: current.companyRoleIds ?? null,
                competingBidderIds: current.competingBidderIds ?? null,
                bidCategoryIds: current.bidCategoryIds ?? null,
                opportunityDueDate: current.opportunityDueDate ?? null,
                expectedResponseDate: current.expectedResponseDate ?? null,
                potentialValue: current.potentialValue ?? null,
                probabilityValue: current.probabilityValue ?? null,
                includeArchived: current.includeArchived ?? null,
                includeOpportunityArchived: current.includeOpportunityArchived ?? null,
                stateId: current.stateId ?? null,
                buildingClassId: current.buildingClassId ?? null,
                bidDate: current.bidDate ?? null,
                awardedDate: current.awardedDate ?? null,
                bidAmount: current.bidAmount ?? null,
                awardedAmount: current.awardedAmount ?? null,
                estimateTypeId: current.estimateTypeId ?? null
            },
            { emitEvent: true }
        );
    }

    private buildAdditionalFilters(): Partial<IFilterValues> {
        const {
            projectDueDate,
            opportunityCreationDate,
            projectStartDate,
            projectEndDate,
            projectStatusIds,
            changeOrderStatusIds,
            sellPriceValue,
            markets,
            leads,
            divisionIds,
            customerIds,
            companyIds,
            accountIds,
            companyTypeId,
            relatedOpportunitiesSalesPersonIds,
            opportunityStatusIds,
            productTypesIds,
            companyRoleIds,
            competingBidderIds,
            bidCategoryIds,
            opportunityDueDate,
            expectedResponseDate,
            potentialValue,
            probabilityValue,
            includeArchived,
            includeOpportunityArchived,
            stateId,
            buildingClassId,
            bidDate,
            awardedDate,
            bidAmount,
            awardedAmount,
            estimateTypeId
        } = this.form.getRawValue();

        const ids = (v: number[] | null | undefined) => (v?.length ? v : null);

        return {
            projectDueDate: {
                from: projectDueDate?.from ?? null,
                to: projectDueDate?.to ?? null
            },
            opportunityCreationDate: {
                from: opportunityCreationDate?.from ?? null,
                to: opportunityCreationDate?.to ?? null,
                includeEmpty: opportunityCreationDate?.includeEmpty ?? null
            },
            projectStartDate: {
                from: projectStartDate?.from ?? null,
                to: projectStartDate?.to ?? null,
                includeEmpty: projectStartDate?.includeEmpty ?? null
            },
            projectEndDate: {
                from: projectEndDate?.from ?? null,
                to: projectEndDate?.to ?? null,
                includeEmpty: projectEndDate?.includeEmpty ?? null
            },
            projectStatusIds: ids(projectStatusIds),
            changeOrderStatusIds: ids(changeOrderStatusIds),
            sellPriceValue: {
                from: sellPriceValue?.from ?? null,
                to: sellPriceValue?.to ?? null
            },
            markets: ids(markets),
            leads: ids(leads),
            divisionIds: ids(divisionIds),
            customerIds: ids(customerIds),
            companyIds: ids(companyIds),
            accountIds: ids(accountIds),
            companyTypeId: companyTypeId ?? null,
            relatedOpportunitiesSalesPersonIds: ids(relatedOpportunitiesSalesPersonIds),
            opportunityStatusIds: ids(opportunityStatusIds),
            productTypesIds: ids(productTypesIds),
            companyRoleIds: ids(companyRoleIds),
            competingBidderIds: ids(competingBidderIds),
            bidCategoryIds: ids(bidCategoryIds),
            opportunityDueDate: {
                from: opportunityDueDate?.from ?? null,
                to: opportunityDueDate?.to ?? null,
                includeEmpty: opportunityDueDate?.includeEmpty ?? null
            },
            expectedResponseDate: {
                from: expectedResponseDate?.from ?? null,
                to: expectedResponseDate?.to ?? null,
                includeEmpty: expectedResponseDate?.includeEmpty ?? null
            },
            potentialValue: {
                from: potentialValue?.from ?? null,
                to: potentialValue?.to ?? null
            },
            probabilityValue: {
                from: probabilityValue?.from ?? null,
                to: probabilityValue?.to ?? null
            },
            includeArchived: includeArchived ?? null,
            includeOpportunityArchived: includeOpportunityArchived ?? null,
            stateId: stateId ?? null,
            buildingClassId: buildingClassId ?? null,
            bidDate: {
                from: bidDate?.from ?? null,
                to: bidDate?.to ?? null
            },
            awardedDate: {
                from: awardedDate?.from ?? null,
                to: awardedDate?.to ?? null
            },
            bidAmount: {
                from: bidAmount?.from ?? null,
                to: bidAmount?.to ?? null
            },
            awardedAmount: {
                from: awardedAmount?.from ?? null,
                to: awardedAmount?.to ?? null
            },
            estimateTypeId: estimateTypeId ?? null
        };
    }

    private persistAdditionalFilters(): void {
        const additionalFilters = this.buildAdditionalFilters();
        this.service.updateFilterValues(additionalFilters);
        this.reportFiltersStateService.setAdditionalFilters(additionalFilters);
    }

    private hasAnySelectedFilter(value: Record<string, unknown>, includeEmptyAsSelection = false): boolean {
        return Object.values(value).some((current) => {
            if (current == null) return false;
            if (Array.isArray(current)) return current.length > 0;
            if (typeof current === 'object') {
                const obj = current as Record<string, unknown>;
                if ('from' in obj || 'to' in obj) {
                    return !!obj['from'] || !!obj['to'] || (includeEmptyAsSelection && obj['includeEmpty'] === true);
                }
            }
            return !!current;
        });
    }
}
